/**
 * Alert Preferences Page
 *
 * Allows users to configure their alert notification preferences
 * for each alert type and notification channel.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/shared/Navbar';
import { useAlerts } from '../contexts/AlertContext';
import {
  AlertType,
  AlertSeverity,
  realtimeAlertApi,
} from '../services/realtime-alert.service';
import type { AlertPreference } from '../services/realtime-alert.service';
import {
  showTestNotification,
  requestNotificationPermission,
  getNotificationPermission,
  formatAlertType,
} from '../utils/browser-notifications';

const AlertPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const { notificationPermission, requestNotifications } = useAlerts();

  const [preferences, setPreferences] = useState<AlertPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await realtimeAlertApi.getPreferences();
        setPreferences(prefs);
      } catch (err) {
        setError('Failed to load preferences');
        console.error('Error loading preferences:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadPreferences();
  }, []);

  // Update a preference
  const updatePreference = async (
    alertType: AlertType,
    field: keyof AlertPreference,
    value: boolean | AlertSeverity
  ) => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await realtimeAlertApi.updatePreferences(alertType, { [field]: value });

      // Update local state
      setPreferences(prev =>
        prev.map(p =>
          p.alertType === alertType ? { ...p, [field]: value } : p
        )
      );

      setSuccessMessage('Preferences saved');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save preference');
      console.error('Error saving preference:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle test notification
  const handleTestNotification = () => {
    showTestNotification();
    setSuccessMessage('Test notification sent');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle enable notifications
  const handleEnableNotifications = async () => {
    await requestNotifications();
  };

  // Get preference for alert type
  const getPreference = (alertType: AlertType): AlertPreference | undefined => {
    return preferences.find(p => p.alertType === alertType);
  };

  // Severity options
  const severityOptions = [
    { value: AlertSeverity.INFO, label: 'All (Info and above)' },
    { value: AlertSeverity.WARNING, label: 'Warning and above' },
    { value: AlertSeverity.HIGH, label: 'High and above' },
    { value: AlertSeverity.CRITICAL, label: 'Critical only' },
  ];

  // Alert type descriptions
  const alertTypeDescriptions: Record<AlertType, string> = {
    [AlertType.SHELL_GAME_DETECTED]:
      'VINs being used across multiple carriers indicating potential fraud',
    [AlertType.CRITICAL_VIOLATION]: 'Severe safety violations requiring immediate attention',
    [AlertType.FATAL_CRASH]: 'Fatal accidents involving monitored carriers',
    [AlertType.OOS_ORDER_ISSUED]: 'Out-of-service orders issued to carriers',
    [AlertType.BASIC_THRESHOLD_CROSSED]:
      'Carrier BASIC scores crossing intervention thresholds',
    [AlertType.CONTACT_MATCH_FOUND]:
      'Multiple carriers sharing contact information (phone, address)',
    [AlertType.RISK_SCORE_INCREASED]: 'Significant increases in carrier risk scores',
    [AlertType.NEW_CARRIER_IN_NETWORK]:
      'New carriers detected in fraud networks',
    [AlertType.INSURANCE_EXPIRING]:
      'Carrier insurance (BIPD/Cargo) expiring within 90 days',
    [AlertType.AUTHORITY_REVOKED]:
      'Carrier operating authority has been revoked or suspended',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate('/alerts')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Alert Preferences</h1>
          </div>
          <p className="text-gray-500 ml-12">
            Configure which alerts you receive and how you're notified
          </p>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {successMessage}
          </div>
        )}

        {/* Browser Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Browser Notifications</h2>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700">
                {notificationPermission === 'granted'
                  ? 'Browser notifications are enabled'
                  : notificationPermission === 'denied'
                  ? 'Browser notifications are blocked'
                  : 'Enable browser notifications for real-time alerts'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {notificationPermission === 'denied'
                  ? 'Please enable notifications in your browser settings'
                  : 'Get notified even when the app is in the background'}
              </p>
            </div>

            <div className="flex gap-2">
              {notificationPermission !== 'granted' && notificationPermission !== 'denied' && (
                <button
                  onClick={handleEnableNotifications}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enable
                </button>
              )}
              {notificationPermission === 'granted' && (
                <button
                  onClick={handleTestNotification}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Send Test
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Alert Types */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Alert Types</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configure notification preferences for each alert type
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {Object.values(AlertType).map(alertType => {
              const pref = getPreference(alertType);
              if (!pref) return null;

              return (
                <div key={alertType} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">
                        {formatAlertType(alertType)}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {alertTypeDescriptions[alertType]}
                      </p>
                    </div>

                    {/* Enable Toggle */}
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pref.enabled}
                        onChange={e => updatePreference(alertType, 'enabled', e.target.checked)}
                        disabled={isSaving}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {pref.enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Min Severity */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Minimum Severity
                        </label>
                        <select
                          value={pref.minSeverity}
                          onChange={e =>
                            updatePreference(
                              alertType,
                              'minSeverity',
                              e.target.value as AlertSeverity
                            )
                          }
                          disabled={isSaving}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {severityOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Notification Channels */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notification Channels
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={pref.inAppNotify}
                              onChange={e =>
                                updatePreference(alertType, 'inAppNotify', e.target.checked)
                              }
                              disabled={isSaving}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">In-App</span>
                          </label>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={pref.browserNotify}
                              onChange={e =>
                                updatePreference(alertType, 'browserNotify', e.target.checked)
                              }
                              disabled={isSaving || notificationPermission !== 'granted'}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Browser</span>
                          </label>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={pref.emailNotify}
                              onChange={e =>
                                updatePreference(alertType, 'emailNotify', e.target.checked)
                              }
                              disabled={isSaving}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Email</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Saving Indicator */}
        {isSaving && (
          <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg">
            Saving...
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertPreferencesPage;
